/**
 * Control utilities for form builder
 */

import type { FormControlType } from '@atlas-forms/types-js';
import {
  EXPRESSION_CONTROL_DEFAULT_FORMAT,
  EXPRESSION_CONTROL_DEFAULT_SYNTAX,
} from '@atlas-forms/controls-advanced-react';

/**
 * Group controls by category
 */
export function groupControlsByCategory(): Record<string, FormControlType[]> {
  return {
    'Input Controls': [
      'text',
      'textarea',
      'number',
      'email',
      'url',
      'password',
      'select',
      'multiselect',
      'checkbox',
      'radio',
      'switch',
      'date',
      'datetime',
      'button',
      'json-editor',
    ] as FormControlType[],
    'Display Controls': [
      'label',
      'header',
      'image',
      'video',
      'audio',
      'mermaid',
      'article',
      'html',
      'css',
      'file-upload',
      'pdf-viewer',
      'iframe-viewer',
      'tree-view',
      'org-chart',
    ] as FormControlType[],
    'Chart Controls': [
      'bar-chart',
      'line-chart',
      'pie-chart',
      'area-chart',
      'scatter-plot',
      'bubble-chart',
      'histogram',
      'waterfall-chart',
      'tree-map',
      'heatmap',
      'sankey-diagram',
      'network-graph',
      'gantt-chart',
    ] as FormControlType[],
    'Gauge & Indicators': [
      'circular-gauge',
      'linear-gauge',
      'thermometer',
      'progress-ring',
      'password-strength-meter',
      'kpi-card',
    ] as FormControlType[],
    'Layout & Structure': [
      'form-container',
      'card-container',
      'collapsible-panel',
      'modal',
      'enhanced-tabs',
      'accordion',
      'stepper',
      'breadcrumb',
      'sidebar-nav',
      'data-table',
      'api-response-viewer',
      'variable-inspector',
      'timeline',
      'conditional-logic-viewer',
      'analytics-dashboard',
      'database-query-builder',
    ] as FormControlType[],
    'Advanced Input': [
      'rich-text-editor',
      'code-editor',
      'color-picker',
      'slider-range',
      'date-range-picker',
      'time-picker',
      'location-picker',
      'tree-select',
      'cascading-select',
      'multi-select-search',
      'tag-input',
      'enhanced-json-editor',
      'key-value-pairs',
      'form-picker',
      'expression',
    ] as FormControlType[],
    'File & Code': [
      'css-file-selector',
      'js-file-selector',
      'javascript-block',
      'style-block',
      'html-block',
      'asset-manager',
    ] as FormControlType[],
    'Media Controls': [
      'image-gallery',
      'enhanced-video-player',
      'audio-playlist',
      'document-viewer',
      'map',
      'qr-code-generator',
      'barcode-generator',
      'captcha-widget',
      'signature-pad',
      'document-scanner',
    ] as FormControlType[],
    'Other': [
      'grid',
      'table',
      'link',
      'tab',
      'custom',
      'server-pagination',
    ] as FormControlType[],
  };
}

/**
 * Get all controls (flattened)
 */
export function getAllControls(): FormControlType[] {
  const categories = groupControlsByCategory();
  return Object.values(categories).flat();
}

/**
 * Search controls by name
 */
export function searchControls(term: string): FormControlType[] {
  const allControls = getAllControls();
  return allControls.filter((control) =>
    control.toLowerCase().includes(term.toLowerCase())
  );
}

/**
 * Get control by type
 */
export function getControlByType(type: FormControlType): FormControlType | null {
  return getAllControls().includes(type) ? type : null;
}

/**
 * Get category for control type
 */
export function getCategoryForControl(type: FormControlType): string | null {
  const categories = groupControlsByCategory();
  for (const [category, controls] of Object.entries(categories)) {
    if (controls.includes(type)) {
      return category;
    }
  }
  return null;
}

/**
 * Check if control type requires configuration
 */
export function requiresConfiguration(type: FormControlType): boolean {
  const configurable = [
    'select',
    'multiselect',
    'radio',
    'checkbox',
    'date-range-picker',
    'cascading-select',
    'tree-select',
  ];
  return configurable.includes(type);
}

/**
 * Get a human-readable label from a control type string (e.g. 'date-range-picker' → 'Date Range Picker')
 */
export function getControlLabel(type: string): string {
  return type
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get the default label for a control type, with friendly overrides for common types.
 */
export function getDefaultLabel(type: string): string {
  const typeToLabel: Record<string, string> = {
    text: 'Text Field',
    textarea: 'Text Area',
    email: 'Email',
    url: 'URL',
    tel: 'Phone',
    password: 'Password',
    number: 'Number',
    checkbox: 'Checkbox',
    radio: 'Radio Button',
    select: 'Select',
    multiselect: 'Multi-select',
    switch: 'Toggle Switch',
    date: 'Date',
    datetime: 'Date & Time',
    'json-editor': 'JSON Editor',
    label: 'Label',
    header: 'Header',
    image: 'Image',
    video: 'Video',
    audio: 'Audio',
    button: 'Button',
    'form-container': 'Form Container',
    tab: 'Tab',
    'tree-view': 'Tree View',
    'org-chart': 'Org Chart',
    'form-picker': 'Form Picker',
    expression: 'Expression Builder',
  };
  return typeToLabel[type] || getControlLabel(type);
}

/**
 * Get default config for control type
 */
export function getDefaultConfig(type: FormControlType): Record<string, unknown> {
  const configs: Record<FormControlType, Record<string, unknown>> = {
    text: { maxLength: 255 },
    textarea: { maxLength: 1000, rows: 4 },
    email: { pattern: '^[\\w.-]+@[\\w.-]+\\.\\w+$' },
    url: { pattern: '^https?://' },
    tel: {},
    password: { minLength: 8 },
    number: { min: 0, max: 100 },
    select: { options: [] },
    multiselect: { options: [] },
    checkbox: {},
    radio: { options: [] },
    switch: { on: 'On', off: 'Off' },
    date: { format: 'YYYY-MM-DD' },
    datetime: { format: 'YYYY-MM-DD HH:mm' },
    'json-editor': { mode: 'tree' },
    label: {},
    header: { level: 1 },
    image: { width: '100%', height: 'auto' },
    video: { width: '100%', height: 'auto' },
    audio: {},
    mermaid: { diagram: '' },
    article: {},
    html: { content: '' },
    css: { content: '' },
    'file-upload': { accept: '*', multiple: false },
    'pdf-viewer': {},
    'iframe-viewer': { src: '' },
    'bar-chart': { type: 'bar' },
    'line-chart': { type: 'line' },
    'pie-chart': { type: 'pie' },
    'area-chart': { type: 'area' },
    'scatter-plot': { type: 'scatter' },
    'bubble-chart': { type: 'bubble' },
    histogram: { type: 'histogram' },
    'waterfall-chart': { type: 'waterfall' },
    'tree-map': { type: 'tree-map' },
    heatmap: { type: 'heatmap' },
    'sankey-diagram': { type: 'sankey' },
    'network-graph': { type: 'network' },
    'gantt-chart': { type: 'gantt' },
    'circular-gauge': { min: 0, max: 100 },
    'linear-gauge': { min: 0, max: 100 },
    thermometer: { min: -50, max: 50 },
    'progress-ring': { min: 0, max: 100 },
    'password-strength-meter': {},
    'kpi-card': { value: 0, label: '' },
    'card-container': {},
    'collapsible-panel': { collapsed: false },
    modal: { title: '', size: 'md' },
    'enhanced-tabs': { tabs: [] },
    accordion: { items: [] },
    stepper: { steps: [], currentStep: 0 },
    breadcrumb: { items: [] },
    'sidebar-nav': { items: [] },
    'data-table': { columns: [] },
    'api-response-viewer': { endpoint: '' },
    'variable-inspector': {},
    timeline: { items: [] },
    'conditional-logic-viewer': {},
    'analytics-dashboard': {},
    'database-query-builder': {},
    'form-container': { childForms: [], layout: 'tabs-top', validationMode: 'free' },
    'rich-text-editor': { toolbar: ['bold', 'italic', 'underline'] },
    'code-editor': { language: 'javascript' },
    'color-picker': { format: 'hex' },
    'slider-range': { min: 0, max: 100 },
    'date-range-picker': { format: 'YYYY-MM-DD' },
    'time-picker': { format: 'HH:mm' },
    'location-picker': { zoom: 13 },
    'tree-select': { options: [] },
    'cascading-select': { options: [] },
    'multi-select-search': { options: [] },
    'tag-input': {},
    'enhanced-json-editor': { mode: 'tree' },
    'key-value-pairs': {},
    'css-file-selector': {},
    'js-file-selector': {},
    'javascript-block': { code: '' },
    'style-block': { code: '' },
    'html-block': { code: '' },
    'asset-manager': {},
    'image-gallery': { images: [] },
    'enhanced-video-player': { src: '' },
    'audio-playlist': { tracks: [] },
    'document-viewer': { src: '' },
    map: { lat: 0, lng: 0, zoom: 13 },
    'qr-code-generator': { text: '' },
    'barcode-generator': { text: '' },
    'captcha-widget': { type: 'recaptcha' },
    'signature-pad': {},
    'document-scanner': {},
    button: { text: 'Submit', type: 'button' },
    grid: { columns: 12 },
    table: { rows: [], columns: [] },
    link: { href: '#', text: 'Link' },
    tab: { tabs: [] },
    custom: {},
    // Mirrors `server-pagination`'s real config shape in `FormField.tsx` (control.types.ts's
    // doc comment on the type explains the control itself) — `resultsControlID` names the grid
    // control whose row count Prev/Next uses to detect "paged past the last page".
    'server-pagination': { resultsControlID: '' },
    // Form-scope plugin controls — default configs mirror formSettings.md
    'seo-settings':          { metaTitle: '', metaDescription: '', ogTitle: '', ogDescription: '', ogImage: '', robotsDirective: 'index', canonicalUrl: '', slugUrl: '', faviconUrl: '' },
    'analytics-settings':    { googleAnalyticsId: '', googleTagManagerId: '', facebookPixelId: '', linkedInInsightTagId: '', conversionEventName: '', utmCaptureEnabled: false, trackPartialSubmissions: false },
    'security-settings':     { captchaType: 'none', honeypotEnabled: false, passwordProtected: false, formPassword: '', submissionsPerIpLimit: 0, rateLimitWindowMinutes: 60, closedAfterDate: '', closedAfterCount: 0 },
    'compliance-settings':   { gdprConsentEnabled: false, gdprConsentText: '', dataRetentionDays: 0, hipaaMode: false, auditTrailEnabled: false, privacyPolicyUrl: '', termsUrl: '' },
    'branding-settings':     { logoUrl: '', logoPosition: 'left', primaryColor: '', accentColor: '', backgroundColor: '', fontFamily: '', headerHtml: '', footerHtml: '', hidePoweredByBadge: false },
    'behavior-settings':     { confirmationType: 'message', confirmationMessage: 'Thank you!', redirectUrl: '', redirectDelaySeconds: 0, allowMultipleSubmissions: true, autoSaveEnabled: false, showProgressBar: false },
    'notification-settings': { notificationSubject: '', autoResponderEnabled: false, autoResponderEmailField: '', autoResponderSubject: '' },
    'embed-settings':        { embedType: 'inline', popupTrigger: 'load', popupDelaySeconds: 0, iframeAutoResize: true, shareLinkEnabled: false, qrCodeEnabled: false },
    'custom-code':           { headHtml: '', bodyStartHtml: '', bodyEndHtml: '', inlineStyles: '' },
    'tree-view': { idField: 'id', labelField: 'name', childrenField: 'children', showLines: true, showIcons: true, defaultExpanded: true },
    'org-chart': { idField: 'id', nameField: 'name', titleField: 'title', childrenField: 'children', layout: 'vertical', cardStyle: 'detailed' },
    'editable-grid': { columns: [], allowAdd: true, allowDelete: true },
    'display-grid': { columns: [] },
    toggle: { on: 'On', off: 'Off' },
    'user-picker': { multiple: false },
    'actor-list': { multiple: true },
    'form-picker': {},
    // Mirrors `ExpressionControlConfig` in `@atlas-forms/controls-advanced-react` and the two
    // fields `ExpressionEditor`'s property panel writes. Required, not optional: this map is typed
    // `Record<FormControlType, …>`, so adding `'expression'` to the union without an entry here is
    // a compile error.
    //
    // The two values are the control's own exported constants rather than a third independent copy
    // of the same literals. `getDefaultConfig` has no caller anywhere in this repo today, so this
    // entry is currently unreachable — but "unreachable today" is not a guarantee, and a future
    // caller deserves a config that agrees with what the control actually falls back to rather than
    // one that silently froze at whatever the defaults were on the day it was written.
    expression: {
      syntax: EXPRESSION_CONTROL_DEFAULT_SYNTAX,
      outputFormat: EXPRESSION_CONTROL_DEFAULT_FORMAT,
    },
    // Form-scope plugin controls contributed by @atlas-forms/controls-form-actions-react —
    // default configs mirror each plugin's own `defaultConfig` in its `definition.ts`.
    'action-library-plugin': { libraryId: '', actions: [] },
    'api-actions-plugin': { apiActions: [] },
    'cross-field-rules-plugin': { rules: [] },
    'field-groups-plugin': { groups: [] },
    'form-context-plugin': {},
    'form-schema-settings-plugin': {},
    'host-environment-plugin': {},
  };
  return configs[type] || {};
}
